import type { Observable } from 'rxjs';
import type { ODataClient } from '../client';
import type {
  ODataActionOptions,
  ODataActionResource,
  ODataEntities,
  ODataEntity,
  ODataFunctionOptions,
  ODataFunctionResource,
  ODataNavigationPropertyResource,
  ODataProperty,
  ODataQueryableOptions,
} from '../resources';

export abstract class ODataBaseService {
  constructor(
    protected client: ODataClient,
    protected name: string,
    protected apiNameOrEntityType?: string,
  ) {}

  get api() {
    return this.client.apiFor(this.apiNameOrEntityType);
  }

  protected callFunction<P, R>(
    params: P | null,
    resource: ODataFunctionResource<P, R>,
    responseType: 'entity',
    options?: ODataFunctionOptions<R>,
  ): Observable<ODataEntity<R>>;
  protected callFunction<P, R>(
    params: P | null,
    resource: ODataFunctionResource<P, R>,
    responseType: 'entities',
    options?: ODataFunctionOptions<R>,
  ): Observable<ODataEntities<R>>;
  protected callFunction<P, R>(
    params: P | null,
    resource: ODataFunctionResource<P, R>,
    responseType: 'property',
    options?: ODataFunctionOptions<R>,
  ): Observable<ODataProperty<R>>;
  protected callFunction<P, R>(
    params: P | null,
    resource: ODataFunctionResource<P, R>,
    responseType: 'none',
    options?: ODataFunctionOptions<R>,
  ): Observable<null>;
  protected callFunction<P, R>(
    params: P | null,
    resource: ODataFunctionResource<P, R>,
    responseType: 'blob',
    options?: ODataFunctionOptions<R>,
  ): Observable<Blob>;
  protected callFunction<P, R>(
    params: P | null,
    resource: ODataFunctionResource<P, R>,
    responseType: 'arraybuffer',
    options?: ODataFunctionOptions<R>,
  ): Observable<ArrayBuffer>;
  protected callFunction<P, R>(
    params: P | null,
    resource: ODataFunctionResource<P, R>,
    responseType: 'property' | 'entity' | 'entities' | 'none' | 'blob' | 'arraybuffer',
    options: ODataFunctionOptions<R> = {},
  ): Observable<any> {
    if (options.query !== undefined) resource = resource.query(options.query);
    return resource.call(params, {
      responseType: responseType as any,
      ...options,
    });
  }

  protected callAction<P, R>(
    params: P | null,
    resource: ODataActionResource<P, R>,
    responseType: 'entity',
    options?: ODataActionOptions<R>,
  ): Observable<ODataEntity<R>>;
  protected callAction<P, R>(
    params: P | null,
    resource: ODataActionResource<P, R>,
    responseType: 'entities',
    options?: ODataActionOptions<R>,
  ): Observable<ODataEntities<R>>;
  protected callAction<P, R>(
    params: P | null,
    resource: ODataActionResource<P, R>,
    responseType: 'property',
    options?: ODataActionOptions<R>,
  ): Observable<ODataProperty<R>>;
  protected callAction<P, R>(
    params: P | null,
    resource: ODataActionResource<P, R>,
    responseType: 'none',
    options?: ODataActionOptions<R>,
  ): Observable<null>;
  protected callAction<P, R>(
    params: P | null,
    resource: ODataActionResource<P, R>,
    responseType: 'blob',
    options?: ODataActionOptions<R>,
  ): Observable<Blob>;
  protected callAction<P, R>(
    params: P | null,
    resource: ODataActionResource<P, R>,
    responseType: 'arraybuffer',
    options?: ODataActionOptions<R>,
  ): Observable<ArrayBuffer>;
  protected callAction<P, R>(
    params: P | null,
    resource: ODataActionResource<P, R>,
    responseType: 'property' | 'entity' | 'entities' | 'none' | 'blob' | 'arraybuffer',
    options: ODataActionOptions<R> = {},
  ): Observable<any> {
    if (options.query !== undefined) resource = resource.query(options.query);
    return resource.call(params, {
      responseType: responseType as any,
      ...options,
    });
  }

  protected fetchNavigationProperty<S>(
    resource: ODataNavigationPropertyResource<S>,
    responseType: 'entity',
    options?: ODataQueryableOptions<S>,
  ): Observable<ODataEntity<S>>;
  protected fetchNavigationProperty<S>(
    resource: ODataNavigationPropertyResource<S>,
    responseType: 'entities',
    options?: ODataQueryableOptions<S>,
  ): Observable<ODataEntities<S>>;
  protected fetchNavigationProperty<S>(
    resource: ODataNavigationPropertyResource<S>,
    responseType: 'entity' | 'entities',
    options: ODataQueryableOptions<S> = {},
  ): Observable<any> {
    if (options.query !== undefined) resource = resource.query(options.query);
    return resource.fetch({ responseType: responseType as any, ...options });
  }
}
